import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Sparkles, Wand2, MessageSquareText, Library, Zap, ArrowRight, TrendingUp } from "lucide-react";
import AppShell from "@/components/AppShell";
import api, { fileUrl } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

const tools = [
    { to: "/enhance", icon: Sparkles, title: "AI Enhancer", desc: "Phone snap → studio shot", color: "orange" },
    { to: "/background", icon: Wand2, title: "Backgrounds", desc: "Swap to marble, wood, dark moody", color: "rose" },
    { to: "/captions", icon: MessageSquareText, title: "Captions", desc: "Hashtags + bilingual EN/ES", color: "amber" },
    { to: "/library", icon: Library, title: "Library", desc: "Browse your gallery", color: "zinc" },
];

export default function Dashboard() {
    const { user } = useAuth();
    const [items, setItems] = useState([]);
    const [suggestions, setSuggestions] = useState("");
    const [loadingSugg, setLoadingSugg] = useState(false);

    useEffect(() => {
        api.get("/library").then((r) => setItems(r.data.items || [])).catch(() => {});
    }, []);

    const fetchSuggestions = async () => {
        setLoadingSugg(true);
        try {
            const { data } = await api.post("/growth/suggestions", { food_type: user?.restaurant_name || "modern restaurant" });
            setSuggestions(data.suggestions);
        } catch (_e) {
            setSuggestions("Unable to fetch suggestions right now.");
        } finally {
            setLoadingSugg(false);
        }
    };

    return (
        <AppShell
            subtitle={`Welcome back${user?.name ? `, ${user.name.split(" ")[0]}` : ""}`}
            title="Your kitchen, in pixels."
            action={
                <Link to="/enhance" data-testid="dash-new-enhance-btn" className="btn-magic px-5 py-3 rounded-full text-white font-semibold inline-flex items-center gap-2 w-fit">
                    <Sparkles className="w-4 h-4" />
                    New enhancement
                </Link>
            }
        >
            {/* Tools bento */}
            <div className="grid grid-cols-1 md:grid-cols-12 gap-5 mb-12">
                {tools.map((t, i) => (
                    <motion.div
                        key={t.to}
                        initial={{ opacity: 0, y: 18 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: i * 0.06 }}
                        className="md:col-span-3"
                    >
                        <Link to={t.to} data-testid={`dash-tool-${t.to.slice(1)}`} className="glass-card rounded-2xl p-6 lift block h-full">
                            <div className={`h-9 w-9 grid place-items-center rounded-lg mb-4 ${t.color === "orange" ? "bg-orange-500/15 text-orange-400" : t.color === "rose" ? "bg-rose-500/15 text-rose-400" : t.color === "amber" ? "bg-amber-500/15 text-amber-400" : "bg-white/8 text-zinc-300"}`}>
                                <t.icon className="w-4 h-4" />
                            </div>
                            <div className="font-heading text-lg font-semibold tracking-tight">{t.title}</div>
                            <div className="text-sm text-zinc-500 mt-1">{t.desc}</div>
                        </Link>
                    </motion.div>
                ))}
            </div>

            {/* Stats */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-12">
                <div className="glass-card rounded-2xl p-6" data-testid="stat-credits">
                    <div className="text-xs font-mono uppercase tracking-widest text-zinc-500">Credits remaining</div>
                    <div className="font-heading text-4xl font-bold mt-2">{user?.credits_remaining ?? 0}</div>
                    <Link to="/pricing" className="text-xs text-orange-400 hover:text-orange-300 mt-3 inline-flex items-center gap-1">
                        Upgrade plan <ArrowRight className="w-3 h-3" />
                    </Link>
                </div>
                <div className="glass-card rounded-2xl p-6" data-testid="stat-enhanced">
                    <div className="text-xs font-mono uppercase tracking-widest text-zinc-500">Images enhanced</div>
                    <div className="font-heading text-4xl font-bold mt-2">{items.length}</div>
                    <div className="text-xs text-zinc-500 mt-3">All time</div>
                </div>
                <div className="glass-card rounded-2xl p-6">
                    <div className="text-xs font-mono uppercase tracking-widest text-zinc-500">Current plan</div>
                    <div className="font-heading text-2xl font-bold mt-2 capitalize">{user?.plan === "trial" ? "Free trial" : user?.plan}</div>
                    <div className="text-xs text-zinc-500 mt-3">{user?.plan === "trial" ? "Upgrade for more credits" : "Active subscription"}</div>
                </div>
            </div>

            {/* AI growth suggestions */}
            <div className="glass-card rounded-3xl p-7 md:p-9 mb-12">
                <div className="flex items-start justify-between mb-5 flex-wrap gap-3">
                    <div>
                        <div className="text-[11px] uppercase tracking-[0.22em] text-orange-400 mb-2 font-mono">AI Growth Coach</div>
                        <h3 className="font-heading text-2xl font-semibold tracking-tight">Personalized tips for your menu</h3>
                    </div>
                    <button
                        type="button"
                        onClick={fetchSuggestions}
                        disabled={loadingSugg}
                        data-testid="dash-suggestions-btn"
                        className="px-4 py-2 rounded-full bg-white/5 border border-white/10 hover:bg-white/10 text-sm inline-flex items-center gap-2 disabled:opacity-50"
                    >
                        <TrendingUp className="w-4 h-4" />
                        {loadingSugg ? "Thinking..." : "Get suggestions"}
                    </button>
                </div>
                {suggestions ? (
                    <pre className="whitespace-pre-wrap font-sans text-zinc-300 text-sm leading-relaxed" data-testid="dash-suggestions-output">
                        {suggestions}
                    </pre>
                ) : (
                    <div className="text-sm text-zinc-500">
                        Click "Get suggestions" to receive AI-curated visual marketing tips tuned to your cuisine.
                    </div>
                )}
            </div>

            {/* Recent enhancements */}
            <div>
                <div className="flex items-center justify-between mb-6">
                    <h3 className="font-heading text-2xl font-semibold tracking-tight">Recent enhancements</h3>
                    <Link to="/library" className="text-sm text-zinc-400 hover:text-white inline-flex items-center gap-1">
                        View all <ArrowRight className="w-3.5 h-3.5" />
                    </Link>
                </div>
                {items.length === 0 ? (
                    <div className="glass-card rounded-2xl p-10 text-center" data-testid="dash-empty-state">
                        <Zap className="w-8 h-8 text-orange-400 mx-auto mb-4" />
                        <div className="font-heading text-lg font-semibold mb-2">No enhancements yet</div>
                        <div className="text-sm text-zinc-500 mb-5">Upload your first food photo to see the magic happen.</div>
                        <Link to="/enhance" className="btn-magic px-5 py-2.5 rounded-full text-white font-semibold inline-flex items-center gap-2">
                            Start your first enhancement <ArrowRight className="w-4 h-4" />
                        </Link>
                    </div>
                ) : (
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        {items.slice(0, 8).map((it) => (
                            <div key={it.id} className="aspect-square rounded-xl overflow-hidden border border-white/8 bg-zinc-900">
                                <img src={fileUrl(it.enhanced_path)} alt={it.preset} className="w-full h-full object-cover" />
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </AppShell>
    );
}
